import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Home, BookOpen, Users, ClipboardCheck, Megaphone, Plus, Trash2,
  Upload, Pencil, FileText, Send, Save, Hash, Layers, Image as ImageIcon, Cross, UserCheck,
} from "lucide-react";
import { toast } from "sonner";

// NAV order for admin (per spec): groups → resets → catechists → students → intenciones → content → cms
// Catechist/Student see only their permitted items in the same logical sequence.
const NAV = [
  { id: "overview", to: "/dashboard/catequista", label: "Inicio", icon: Home, roles: ["admin", "catechist"] },
  { id: "groups", to: "/dashboard/catequista#grupos", label: "Grupos y códigos", icon: Hash, roles: ["admin", "catechist"] },
  { id: "resets", to: "/dashboard/catequista#recuperaciones", label: "Recuperaciones de contraseña", icon: ClipboardCheck, roles: ["admin"] },
  { id: "catechists", to: "/dashboard/catequista#catequistas", label: "Catequistas", icon: Users, roles: ["admin"] },
  { id: "students", to: "/dashboard/catequista#estudiantes", label: "Estudiantes inscritos", icon: Users, roles: ["admin", "catechist"] },
  { id: "approvals", to: "/dashboard/catequista#aprobaciones", label: "Aprobaciones pendientes", icon: UserCheck, roles: ["admin"] },
  { id: "intenciones", to: "/dashboard/catequista#intenciones", label: "Intenciones recibidas", icon: Cross, roles: ["admin"] },
  { id: "content", to: "/dashboard/catequista#contenidos", label: "Gestión de contenido", icon: FileText, roles: ["admin"] },
  { id: "site", to: "/dashboard/catequista#sitio", label: "CMS básico", icon: ImageIcon, roles: ["admin"] },
  { id: "topics", to: "/dashboard/catequista#temas", label: "Itinerario formativo", icon: BookOpen, roles: ["admin", "catechist"] },
  { id: "reviews", to: "/dashboard/catequista#revision", label: "Revisar actividades", icon: ClipboardCheck, roles: ["admin", "catechist"] },
  { id: "announcements", to: "/dashboard/catequista#anuncios", label: "Notificaciones", icon: Megaphone, roles: ["admin", "catechist"] },
];

export default function CatechistDashboard() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [topics, setTopics] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [students, setStudents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [editTopic, setEditTopic] = useState(null);
  const [unreadIntenciones, setUnreadIntenciones] = useState(0);
  const [seenIds, setSeenIds] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (user && user.user_id) refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.role]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/intenciones/unread-count").then((r) => setUnreadIntenciones(r.data.count || 0)).catch(() => {});
    api.get("/admin/pending-students").then((r) => setPendingCount(r.data.length || 0)).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    if (!user?.user_id) return;
    if (user.role !== "catechist" && user.role !== "admin") return;
    api.get("/catechist/topics-seen").then((r) => setSeenIds(r.data.seen_topic_ids || [])).catch(() => {});
  }, [user?.user_id, user?.role]);

  const toggleSeen = async (topicId) => {
    const isSeen = seenIds.includes(topicId);
    try {
      if (isSeen) {
        await api.delete(`/catechist/topics-seen/${topicId}`);
        setSeenIds((prev) => prev.filter((x) => x !== topicId));
      } else {
        await api.post(`/catechist/topics-seen/${topicId}`);
        setSeenIds((prev) => [...prev, topicId]);
        toast.success("Catequesis marcada como vista");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar");
    }
  };

  useEffect(() => {
    if (selectedGroup) {
      api.get(`/groups/${selectedGroup}/students`).then((r) => setStudents(r.data));
    } else {
      setStudents([]);
    }
  }, [selectedGroup]);

  const refreshAll = async () => {
    const isAdminRole = user?.role === "admin";
    const calls = [
      api.get("/groups"),
      isAdminRole
        ? Promise.all([api.get("/topics?level=primera_comunion"), api.get("/topics?level=confirmacion")]).then(([a, b]) => ({ data: [...a.data, ...b.data] }))
        : api.get(`/topics?level=${user?.level || "primera_comunion"}`),
      api.get("/submissions"),
      api.get("/announcements"),
    ];
    const [g, t, s, a] = await Promise.all(calls);
    setGroups(g.data);
    setTopics(t.data);
    setSubmissions(s.data);
    setAnnouncements(a.data);
    if (g.data.length && !selectedGroup) setSelectedGroup(g.data[0].code);
  };

  const totalStudents = students.length;
  const pendingReviews = submissions.filter((s) => s.status !== "revisado").length;
  const isAdmin = user?.role === "admin";
  const visibleNav = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  return (
    <DashboardLayout navItems={visibleNav} role="catechist">
      {/* Hero */}
      <section className="mb-10 animate-fade-up" id="overview">
        <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-2">Pastoral Virreyista</p>
        <h1 className="font-display text-4xl md:text-5xl text-primary leading-tight">
          {isAdmin ? "Panel del Administrador" : "Panel del Catequista"}
        </h1>
        <p className="text-foreground/70 mt-2">
          Hola {user?.name}.
          {isAdmin
            ? " Administra usuarios, contenidos y la experiencia completa de la pastoral."
            : ` Gestiona ${user?.level === "confirmacion" ? "Confirmación" : "Primera Comunión"}: estudiantes, actividades y notificaciones.`}
        </p>
        {isAdmin && unreadIntenciones > 0 && (
          <a
            href="#intenciones"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold shadow-sm hover:opacity-90 transition"
            data-testid="int-notif"
          >
            <Cross className="w-4 h-4" /> Tienes {unreadIntenciones} {unreadIntenciones === 1 ? "intención nueva" : "intenciones nuevas"} sin leer
          </a>
        )}
        {isAdmin && pendingCount > 0 && (
          <a
            href="#aprobaciones"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow-sm hover:opacity-90 transition ml-2"
            data-testid="pending-notif"
          >
            <UserCheck className="w-4 h-4" /> {pendingCount} estudiante(s) esperando aprobación
          </a>
        )}
      </section>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Card className="rounded-2xl bg-primary text-primary-foreground border-none" data-testid="stat-groups">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-80">Grupos</p>
            <p className="font-display text-4xl mt-2">{groups.length}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl bg-secondary text-secondary-foreground border-none" data-testid="stat-pending">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-80">Por revisar</p>
            <p className="font-display text-4xl mt-2">{pendingReviews}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Temas</p>
            <p className="font-display text-4xl text-primary mt-2">{topics.length}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Anuncios</p>
            <p className="font-display text-4xl text-primary mt-2">{announcements.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Grupos */}
      <section id="grupos" className="mb-12">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">Gestión</p>
            <h2 className="font-display text-3xl text-primary">Grupos y códigos</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && <NewGroupDialog onCreated={refreshAll} />}
            {isAdmin && <NewCatechistDialog onCreated={refreshAll} />}
            <NewStudentDialog onCreated={refreshAll} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <Card key={g.code} className={`rounded-2xl cursor-pointer transition-all ${selectedGroup === g.code ? "border-2 border-primary shadow-md" : ""}`} onClick={() => setSelectedGroup(g.code)} data-testid={`group-${g.code}`}>
              <CardContent className="p-5">
                <Badge className="bg-secondary text-secondary-foreground uppercase text-[10px] tracking-wider mb-2">{g.level === "primera_comunion" ? "Primera Comunión" : "Confirmación"}</Badge>
                <h3 className="font-display text-xl text-primary">{g.name}</h3>
                <p className="mt-3 text-xs tracking-[0.2em] uppercase font-bold text-foreground/60">Código de acceso</p>
                <p className="font-mono text-2xl font-bold text-primary mt-1">{g.code}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Recuperaciones de contraseña (solo admin) */}
      {user?.role === "admin" && (
        <section id="recuperaciones" className="mb-12">
          <div className="mb-5">
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">Soporte</p>
            <h2 className="font-display text-3xl text-primary">Solicitudes de recuperación de contraseña</h2>
            <p className="text-sm text-foreground/70 mt-1">
              Los enlaces son válidos por 2 horas y de un solo uso. Cópialos y envíalos manualmente al estudiante o catequista.
            </p>
          </div>
          <PasswordResetsPanel />
          <div className="mt-6">
            <AdminSecurityQuestionCard />
          </div>
        </section>
      )}

      {/* Estudiantes del grupo */}
      <section id="estudiantes" className="mb-12">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-3xl text-primary">Estudiantes {selectedGroup && `· ${selectedGroup}`}</h2>
          <span className="text-sm text-foreground/60">{totalStudents} activos</span>
        </div>
        <div className="rounded-2xl bg-card border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="p-3 font-semibold">Nombre</th>
                <th className="p-3 font-semibold">Correo</th>
                <th className="p-3 font-semibold text-center">Temas completados</th>
                <th className="p-3 font-semibold">Estado</th>
                {user?.role === "admin" && <th className="p-3 font-semibold text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {students.length === 0 ? (
                <tr><td colSpan={user?.role === "admin" ? 5 : 4} className="p-10 text-center text-foreground/60">Selecciona un grupo o aún no hay estudiantes registrados.</td></tr>
              ) : students.map((s) => (
                <tr key={s.user_id} className="border-t border-border" data-testid={`stu-${s.user_id}`}>
                  <td className="p-3 font-semibold">{s.name}</td>
                  <td className="p-3 text-foreground/70">{s.email}</td>
                  <td className="p-3 text-center"><Badge className="bg-primary/10 text-primary">{s.progress_count}</Badge></td>
                  <td className="p-3"><Badge className={s.progress_count > 0 ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70"}>{s.progress_count > 0 ? "Activo" : "Inactivo"}</Badge></td>
                  {user?.role === "admin" && (
                    <td className="p-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-full text-destructive hover:bg-destructive/10"
                        data-testid={`del-stu-${s.user_id}`}
                        onClick={async () => {
                          if (!window.confirm(`¿Eliminar a ${s.name} (${s.email})? Esta acción no se puede deshacer.`)) return;
                          try {
                            await api.delete(`/admin/users/${s.user_id}`);
                            toast.success("Estudiante eliminado");
                            setStudents((prev) => prev.filter((x) => x.user_id !== s.user_id));
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "No se pudo eliminar");
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Revisar tareas */}
      <section id="revision" className="mb-12" data-testid="reviews-section">
        <h2 className="font-display text-3xl text-primary mb-5">Revisar tareas</h2>
        {submissions.length === 0 ? (
          <Card className="rounded-2xl"><CardContent className="p-10 text-center text-foreground/60">Aún no hay tareas enviadas.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {submissions.map((s) => (
              <ReviewCard key={s.id} sub={s} topic={topics.find(t => t.id === s.topic_id)} onUpdated={refreshAll} />
            ))}
          </div>
        )}
      </section>

      {/* Itinerario / Temas — catechist en edición acotada + botón vista; admin con edición completa */}
      <section id="temas" className="mb-12">
        <h2 className="font-display text-3xl text-primary mb-2">Itinerario formativo {!isAdmin && user?.level && (<span className="text-base text-foreground/60 font-normal">· {user.level === "primera_comunion" ? "Primera Comunión" : "Confirmación"}</span>)}</h2>
        {!isAdmin && (
          <p className="text-sm text-foreground/60 mb-3">Puedes editar enfoque, recurso bíblico y reflexión de tus temas. Marca cada catequesis como vista al impartirla.</p>
        )}
        {isAdmin ? (
          <Tabs defaultValue="pc">
            <TabsList className="bg-muted rounded-full p-1">
              <TabsTrigger value="pc" className="rounded-full" data-testid="tab-pc">Primera Comunión</TabsTrigger>
              <TabsTrigger value="cf" className="rounded-full" data-testid="tab-cf">Confirmación</TabsTrigger>
            </TabsList>
            <TabsContent value="pc"><TopicsTable topics={topics.filter(t => t.level === "primera_comunion")} onEdit={setEditTopic} canEdit={true} role="admin" seenIds={[]} onToggleSeen={null} /></TabsContent>
            <TabsContent value="cf"><TopicsTable topics={topics.filter(t => t.level === "confirmacion")} onEdit={setEditTopic} canEdit={true} role="admin" seenIds={[]} onToggleSeen={null} /></TabsContent>
          </Tabs>
        ) : (
          <TopicsTable
            topics={topics.filter(t => t.level === (user?.level || "primera_comunion"))}
            onEdit={setEditTopic}
            canEdit={true}
            role="catechist"
            seenIds={seenIds}
            onToggleSeen={toggleSeen}
          />
        )}
      </section>

      {/* Contenidos: Oración + Centenario (sólo admin) */}
      {isAdmin && (
        <section id="contenidos" className="mb-12">
          <div className="mb-5">
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">Gestión de contenido</p>
            <h2 className="font-display text-3xl text-primary">Oración & Centenario</h2>
            <p className="text-sm text-foreground/70 mt-1">Crea, edita y elimina contenidos públicos de las secciones <strong>Oración</strong> y <strong>Centenario</strong>.</p>
          </div>
          <ContentManager />
        </section>
      )}

      {/* Catequistas inscritos (sólo admin) */}
      {/* Catequistas inscritos (sólo admin) */}
      {isAdmin && (
        <section id="aprobaciones" className="mb-12">
          <div className="mb-5">
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">Control de acceso</p>
            <h2 className="font-display text-3xl text-primary">Aprobaciones pendientes</h2>
            <p className="text-sm text-foreground/70 mt-1">Nuevos estudiantes registrados que esperan tu aprobación y asignación de sacramento.</p>
          </div>
          <PendingStudentsPanel groups={groups} />
        </section>
      )}

      {isAdmin && (
        <section id="catequistas" className="mb-12">
          <div className="mb-5">
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">Administración</p>
            <h2 className="font-display text-3xl text-primary">Catequistas inscritos</h2>
            <p className="text-sm text-foreground/70 mt-1">Listado de catequistas registrados en la pastoral.</p>
          </div>
          <CatechistsPanel />
        </section>
      )}

      {/* Intenciones de la Eucaristía (sólo admin) */}
      {isAdmin && (
        <section id="intenciones" className="mb-12">
          <div className="mb-5">
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">Celebración de la Eucaristía</p>
            <h2 className="font-display text-3xl text-primary">Intenciones recibidas</h2>
            <p className="text-sm text-foreground/70 mt-1">Peticiones enviadas por las familias virreyistas desde el formulario público.</p>
          </div>
          <IntencionesPanel />
        </section>
      )}

      {/* Editor del sitio (CMS básico, sólo admin) */}
      {user?.role === "admin" && (
        <section id="sitio" className="mb-12">
          <div className="mb-5">
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">CMS básico</p>
            <h2 className="font-display text-3xl text-primary">Editar textos e imágenes del sitio</h2>
            <p className="text-sm text-foreground/70 mt-1">Personaliza título, subtítulo, párrafo, imagen principal y banner de cada sección pública.</p>
          </div>
          <SiteSettingsEditor />
        </section>
      )}

      {/* Anuncios / Notificaciones */}
      <section id="anuncios" className="mb-12">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-3xl text-primary">Notificaciones pastorales</h2>
          <NewAnnouncementDialog onCreated={refreshAll} />
        </div>
        <div className="space-y-3">
          {announcements.map((a) => (
            <Card key={a.id} className="rounded-2xl" data-testid={`c-ann-${a.id}`}>
              <CardContent className="p-5 flex items-start justify-between gap-4">
                <div>
                  <Badge className="bg-secondary text-secondary-foreground uppercase text-[10px] tracking-wider mb-2">{a.audience}</Badge>
                  <h3 className="font-display text-xl text-primary">{a.title}</h3>
                  <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{a.body}</p>
                </div>
                {isAdmin && (
                  <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/announcements/${a.id}`); toast.success("Eliminado"); refreshAll(); }} data-testid={`del-ann-${a.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <EditTopicDialog topic={editTopic} onClose={() => setEditTopic(null)} onSaved={refreshAll} canEditAll={isAdmin} />
    </DashboardLayout>
  );
}

function TopicsTable({ topics, onEdit, canEdit = true, role = "admin", seenIds = [], onToggleSeen = null }) {
  const showSeen = role === "catechist" && onToggleSeen;
  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden mt-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 text-left">
            <th className="p-3">Fecha</th>
            <th className="p-3">Tema</th>
            <th className="p-3">Ciclo</th>
            {showSeen && <th className="p-3 text-center">Vista</th>}
            {canEdit && <th className="p-3 text-right">Acción</th>}
          </tr>
        </thead>
        <tbody>
          {topics.map((t) => {
            const isSeen = seenIds.includes(t.id);
            return (
              <tr key={t.id} className="border-t border-border" data-testid={`tp-${t.id}`}>
                <td className="p-3 whitespace-nowrap text-foreground/70">{t.date_label}</td>
                <td className="p-3 font-semibold">{t.title}</td>
                <td className="p-3"><Badge variant="outline" className="text-[10px] uppercase">{t.cycle}</Badge></td>
                {showSeen && (
                  <td className="p-3 text-center">
                    <Button
                      size="sm"
                      variant={isSeen ? "default" : "outline"}
                      className="rounded-full"
                      data-testid={`seen-${t.id}`}
                      onClick={() => onToggleSeen(t.id)}
                    >
                      {isSeen ? "✓ Vista" : "Marcar vista"}
                    </Button>
                  </td>
                )}
                {canEdit && (
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => onEdit(t)} className="rounded-full" data-testid={`edit-${t.id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Editar
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EditTopicDialog({ topic, onClose, onSaved, canEditAll = true }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (topic) setForm(topic); }, [topic]);
  if (!topic) return null;

  const save = async () => {
    try {
      const { title, focus, bible_ref, reflection, unit } = form;
      const payload = canEditAll
        ? { title, focus, bible_ref, reflection, unit }
        : { focus, bible_ref, reflection };
      await api.put(`/topics/${topic.id}`, payload);
      toast.success("Tema actualizado");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    }
  };

  return (
    <Dialog open={!!topic} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-primary">Editar tema</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {canEditAll && (
            <>
              <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título" data-testid="et-title" />
              <Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Unidad" />
            </>
          )}
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Recurso bíblico</label>
            <Input className="mt-1" value={form.bible_ref || ""} onChange={(e) => setForm({ ...form, bible_ref: e.target.value })} placeholder="Ej. Lc 11, 9-10" data-testid="et-bible" />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Enfoque</label>
            <Textarea className="mt-1" rows={3} value={form.focus || ""} onChange={(e) => setForm({ ...form, focus: e.target.value })} placeholder="Enfoque pedagógico de la catequesis" data-testid="et-focus" />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Reflexión</label>
            <Textarea className="mt-1" rows={5} value={form.reflection || ""} onChange={(e) => setForm({ ...form, reflection: e.target.value })} placeholder="Reflexión / desarrollo del tema" data-testid="et-reflection" />
          </div>
          {!canEditAll && (
            <p className="text-xs text-foreground/60">Como catequista puedes editar enfoque, recurso bíblico y reflexión. El título y la fecha sólo los modifica el administrador.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancelar</Button>
          <Button onClick={save} className="rounded-full" data-testid="et-save"><Save className="w-4 h-4 mr-1" /> Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewGroupDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", level: "primera_comunion" });
  const submit = async () => {
    try {
      await api.post("/groups", { ...form, code: form.code.toUpperCase() });
      toast.success("Grupo creado");
      setForm({ code: "", name: "", level: "primera_comunion" });
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear");
    }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="new-group-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo grupo</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl text-primary">Crear grupo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Código (ej. PC-2026-A)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="uppercase" data-testid="ng-code" />
            <Input placeholder="Nombre del grupo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ng-name" />
            <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
              <SelectTrigger data-testid="ng-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="primera_comunion">Primera Comunión</SelectItem>
                <SelectItem value="confirmacion">Confirmación</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={submit} className="rounded-full" data-testid="ng-submit">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NewAnnouncementDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", audience: "todos" });
  const submit = async () => {
    try {
      await api.post("/announcements", form);
      toast.success("Anuncio publicado");
      setForm({ title: "", body: "", audience: "todos" });
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error("No se pudo publicar");
    }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="new-ann-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo anuncio</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl text-primary">Publicar anuncio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="na-title" />
            <Textarea rows={4} placeholder="Contenido" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="na-body" />
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="padres">Padres</SelectItem>
                <SelectItem value="estudiantes">Estudiantes</SelectItem>
                <SelectItem value="catequistas">Catequistas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={submit} className="rounded-full" data-testid="na-submit">Publicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ReviewCard({ sub, topic, onUpdated }) {
  const [grade, setGrade] = useState(sub.grade || "");
  const [feedback, setFeedback] = useState(sub.feedback || "");
  const save = async () => {
    await api.put(`/submissions/${sub.id}`, { grade, feedback });
    toast.success("Revisado");
    onUpdated();
  };
  return (
    <Card className="rounded-2xl" data-testid={`rev-${sub.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-secondary font-bold">{sub.user_name} · {sub.group_code}</p>
            <h3 className="font-display text-lg text-primary">{topic?.title || sub.topic_id}</h3>
            {sub.note && <p className="text-sm text-foreground/70 mt-2 italic">“{sub.note}”</p>}
            {sub.file_id && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${sub.file_id}?auth=${localStorage.getItem("cv2026-token") || ""}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary font-semibold mt-2 hover:underline"
                data-testid={`rev-file-${sub.id}`}
              >
                <FileText className="w-3.5 h-3.5" /> Ver archivo enviado
              </a>
            )}
          </div>
          <Badge className={sub.status === "revisado" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}>
            {sub.status === "revisado" ? "Revisado" : "Pendiente"}
          </Badge>
        </div>
        <div className="grid md:grid-cols-[1fr,auto,auto] gap-2 items-start">
          <Textarea rows={2} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Comentario para el estudiante…" data-testid={`fb-${sub.id}`} />
          <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Nota (ej. A, Excelente)" className="md:w-40" data-testid={`gr-${sub.id}`} />
          <Button onClick={save} className="rounded-full" data-testid={`rev-save-${sub.id}`}><Save className="w-4 h-4 mr-1" /> Guardar</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MaterialsManager({ topics }) {
  const [selectedTopic, setSelectedTopic] = useState("");
  const [materials, setMaterials] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!selectedTopic) return setMaterials([]);
    api.get(`/materials/${selectedTopic}`).then(r => setMaterials(r.data));
  }, [selectedTopic]);

  const upload = async () => {
    if (!selectedTopic || !file || !title) return toast.error("Completa el tema, título y archivo");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/materials", { topic_id: selectedTopic, title, file_id: up.data.file_id });
      toast.success("Material agregado");
      setTitle(""); setFile(null);
      const r = await api.get(`/materials/${selectedTopic}`); setMaterials(r.data);
    } catch (e) {
      toast.error("No se pudo subir");
    } finally { setBusy(false); }
  };

  return (
    <Card className="rounded-2xl">
      <CardContent className="p-5 space-y-4">
        <Select value={selectedTopic} onValueChange={setSelectedTopic}>
          <SelectTrigger data-testid="mat-topic"><SelectValue placeholder="Selecciona un tema…" /></SelectTrigger>
          <SelectContent className="max-h-80">
            {topics.map(t => <SelectItem key={t.id} value={t.id}>{t.date_label} · {t.title}</SelectItem>)}
          </SelectContent>
        </Select>
        {selectedTopic && (
          <>
            <div className="grid md:grid-cols-[1fr,1fr,auto] gap-2">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título del material" data-testid="mat-title" />
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.mp4,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="mat-file" />
              <Button onClick={upload} disabled={busy} className="rounded-full" data-testid="mat-upload"><Upload className="w-4 h-4 mr-1" /> Subir</Button>
            </div>
            <div className="space-y-2">
              {materials.length === 0 && <p className="text-sm text-foreground/60">Aún no hay materiales para este tema.</p>}
              {materials.map(m => (
                <div key={m.id} className="flex items-center justify-between p-3 rounded-xl border border-border" data-testid={`mat-${m.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold">{m.title}</span>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${m.file_id}?auth=${localStorage.getItem("cv2026-token") || ""}`}
                      target="_blank" rel="noreferrer"
                      className="text-xs text-primary font-semibold hover:underline"
                    >Abrir</a>
                    <button onClick={async () => { await api.delete(`/materials/${m.id}`); setMaterials(materials.filter(x => x.id !== m.id)); }} className="text-xs text-destructive hover:underline">Eliminar</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ContentManager() {
  const [section, setSection] = useState("oracion");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed; {} = new; {id,...} = edit existing

  const load = async () => {
    const r = await api.get(`/content?section=${section}`);
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [section]);

  const onSaved = async () => { setEditing(null); await load(); };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este contenido?")) return;
    await api.delete(`/content/${id}`);
    toast.success("Contenido eliminado");
    load();
  };

  return (
    <Card className="rounded-2xl" data-testid="content-manager">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="w-60" data-testid="cm-section"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="oracion">Oración</SelectItem>
              <SelectItem value="centenario">Centenario</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setEditing({ section })} className="rounded-full" data-testid="cm-new">
            <Plus className="w-4 h-4 mr-1" /> Nuevo contenido
          </Button>
          <span className="ml-auto text-xs text-foreground/60">{items.length} contenidos</span>
        </div>

        <div className="space-y-2">
          {items.length === 0 && <p className="text-sm text-foreground/60">Aún no hay contenidos para esta sección.</p>}
          {items.map((c) => (
            <div key={c.id} className="flex items-start justify-between gap-3 p-3 rounded-xl border border-border" data-testid={`cm-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {c.category && <Badge className="bg-secondary text-secondary-foreground uppercase text-[10px] tracking-wider">{c.category}</Badge>}
                  <span className="text-[10px] text-foreground/50">orden: {c.order ?? 0}</span>
                </div>
                <p className="font-display text-lg text-primary leading-tight truncate">{c.title}</p>
                <p className="text-xs text-foreground/60 line-clamp-2 mt-1 whitespace-pre-line">{c.body}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(c)} data-testid={`cm-edit-${c.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Editar
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => remove(c.id)} data-testid={`cm-del-${c.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <ContentDialog item={editing} onClose={() => setEditing(null)} onSaved={onSaved} defaultSection={section} />
      </CardContent>
    </Card>
  );
}

function ContentDialog({ item, onClose, onSaved, defaultSection }) {
  const [form, setForm] = useState({ section: defaultSection, title: "", body: "", category: "", image_url: "", order: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        section: item.section || defaultSection,
        title: item.title || "",
        body: item.body || "",
        category: item.category || "",
        image_url: item.image_url || "",
        order: item.order ?? 0,
      });
    }
  }, [item, defaultSection]);

  if (!item) return null;
  const isNew = !item.id;

  const save = async () => {
    if (!form.title || !form.body) { toast.error("Título y texto son obligatorios"); return; }
    setBusy(true);
    try {
      const payload = {
        section: form.section,
        title: form.title,
        body: form.body,
        category: form.category || null,
        image_url: form.image_url || null,
        order: Number(form.order) || 0,
      };
      if (isNew) await api.post("/content", payload);
      else await api.put(`/content/${item.id}`, payload);
      toast.success(isNew ? "Contenido creado" : "Contenido actualizado");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-primary">{isNew ? "Nuevo contenido" : "Editar contenido"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
              <SelectTrigger data-testid="cm-form-section"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="oracion">Oración</SelectItem>
                <SelectItem value="centenario">Centenario</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="Orden (0=primero)" data-testid="cm-form-order" />
          </div>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título" data-testid="cm-form-title" />
          <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Categoría (ej. Oraciones básicas, Rosario, Oración del Papa)" data-testid="cm-form-category" />
          <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL de imagen (opcional)" data-testid="cm-form-image" />
          <Textarea rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Texto de la oración / contenido…" data-testid="cm-form-body" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancelar</Button>
          <Button onClick={save} disabled={busy} className="rounded-full" data-testid="cm-form-save">
            <Save className="w-4 h-4 mr-1" /> {busy ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordResetsPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/password-resets");
      setItems(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = (token) => {
    const link = `${window.location.origin}/reset-password/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Enlace copiado al portapapeles");
  };

  const remove = async (token) => {
    if (!window.confirm("¿Eliminar esta solicitud?")) return;
    await api.delete(`/admin/password-resets/${token}`);
    toast.success("Solicitud eliminada");
    load();
  };

  const pending = items.filter((r) => !r.used && !r.expired);

  return (
    <Card className="rounded-2xl" data-testid="resets-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-foreground/70">
            {busy ? "Cargando…" : `${pending.length} solicitudes activas · ${items.length} en total`}
          </span>
          <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="resets-reload">Actualizar</Button>
        </div>
        {items.length === 0 && !busy && (
          <p className="text-sm text-foreground/60">No hay solicitudes pendientes.</p>
        )}
        <div className="space-y-2">
          {items.map((r) => {
            const link = `${window.location.origin}/reset-password/${r.token}`;
            const status = r.used ? "Usado" : r.expired ? "Expirado" : "Activo";
            const badge = r.used ? "bg-muted text-foreground/70" : r.expired ? "bg-destructive/15 text-destructive" : "bg-primary text-primary-foreground";
            return (
              <div key={r.token} className="rounded-xl border border-border p-4" data-testid={`reset-${r.token}`}>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-display text-lg text-primary leading-tight">{r.user_name || r.email}</p>
                    <p className="text-xs text-foreground/60">{r.email} · <span className="uppercase tracking-wider">{r.user_role}</span></p>
                  </div>
                  <Badge className={`${badge} uppercase text-[10px] tracking-wider`}>{status}{!r.used && !r.expired && ` · ${r.minutes_left} min`}</Badge>
                </div>
                {!r.used && !r.expired && (
                  <div className="flex items-center gap-2 mt-2">
                    <code className="flex-1 text-xs bg-muted border border-border rounded-lg px-3 py-2 truncate" data-testid={`reset-link-${r.token}`}>{link}</code>
                    <Button size="sm" onClick={() => copy(r.token)} className="rounded-full" data-testid={`reset-copy-${r.token}`}>Copiar</Button>
                  </div>
                )}
                <div className="flex justify-end mt-2">
                  <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => remove(r.token)} data-testid={`reset-del-${r.token}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}



function NewCatechistDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", levels: ["primera_comunion"] });
  const [busy, setBusy] = useState(false);
  const toggleLevel = (lv) => {
    setForm((f) => ({
      ...f,
      levels: f.levels.includes(lv) ? f.levels.filter((x) => x !== lv) : [...f.levels, lv],
    }));
  };
  const submit = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Completa todos los campos");
    if (form.levels.length === 0) return toast.error("Asigna al menos un sacramento");
    setBusy(true);
    try {
      await api.post("/admin/catechists", { name: form.name, email: form.email, password: form.password, levels: form.levels });
      toast.success("Catequista creado. Debe cambiar la contraseña en su primer ingreso.");
      setForm({ name: "", email: "", password: "", levels: ["primera_comunion"] });
      setOpen(false); onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear");
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="rounded-full" data-testid="new-cat-btn"><Plus className="w-4 h-4 mr-1" /> Agregar catequista</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl text-primary">Agregar catequista</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nombre completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="nc-name" />
            <Input type="email" placeholder="Correo @virreysolis.edu.co" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="nc-email" />
            <Input type="text" placeholder="Contraseña temporal" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="nc-pwd" />
            <div className="rounded-xl border border-border p-3">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70 mb-2">Sacramentos asignados</p>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.levels.includes("primera_comunion")}
                  onChange={() => toggleLevel("primera_comunion")}
                  className="w-4 h-4 accent-primary"
                  data-testid="nc-lv-pc"
                /> Primera Comunión
              </label>
              <label className="flex items-center gap-2 py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.levels.includes("confirmacion")}
                  onChange={() => toggleLevel("confirmacion")}
                  className="w-4 h-4 accent-primary"
                  data-testid="nc-lv-cf"
                /> Confirmación
              </label>
            </div>
            <p className="text-xs text-foreground/60">El catequista deberá cambiar su contraseña en el primer ingreso.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="nc-submit">Crear catequista</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NewStudentDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", level: "primera_comunion" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Completa todos los campos");
    setBusy(true);
    try {
      await api.post("/admin/students", form);
      toast.success("Estudiante creado. Debe cambiar la contraseña en su primer ingreso.");
      setForm({ name: "", email: "", password: "", level: "primera_comunion" });
      setOpen(false); onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear");
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="rounded-full" data-testid="new-stu-btn"><Plus className="w-4 h-4 mr-1" /> Agregar estudiante</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl text-primary">Agregar estudiante</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nombre completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ns-name" />
            <Input type="email" placeholder="Correo @virreysolis.edu.co" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ns-email" />
            <Input type="text" placeholder="Contraseña temporal" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="ns-pwd" />
            <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
              <SelectTrigger data-testid="ns-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="primera_comunion">Primera Comunión</SelectItem>
                <SelectItem value="confirmacion">Confirmación</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-foreground/60">El estudiante deberá cambiar su contraseña en el primer ingreso. Quedará asignado al grupo {form.level === "primera_comunion" ? "DEMO-PC" : "DEMO-CF"} por defecto.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="ns-submit">Crear estudiante</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



const SITE_SECTIONS = [
  { key: "home", label: "Inicio · Hero" },
  { key: "welcome", label: "Inicio · Bienvenida" },
  { key: "daily", label: "Inicio · Hoy en la catequesis" },
  { key: "dos-caminos", label: "Inicio · Dos caminos" },
  { key: "pilares", label: "Inicio · Pilares (Fraternidad/Espíritu/Eucaristía)" },
  { key: "pc", label: "Primera Comunión" },
  { key: "cf", label: "Confirmación" },
  { key: "oracion", label: "Oración" },
  { key: "centenario", label: "Centenario" },
];

const SITE_FIELDS = [
  { name: "title", label: "Título principal", type: "text" },
  { name: "subtitle", label: "Subtítulo / lema", type: "text" },
  { name: "paragraph", label: "Párrafo descriptivo", type: "textarea" },
  { name: "image_url", label: "Imagen principal", type: "image" },
  { name: "banner_url", label: "Imagen de banner / fondo", type: "image" },
];

function SiteSettingsEditor() {
  const initial = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("cms")) || "home";
  const [section, setSection] = useState(SITE_SECTIONS.find((s) => s.key === initial) ? initial : "home");
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);

  const load = async () => {
    const { data } = await api.get("/site-settings");
    setData(data || {});
  };
  useEffect(() => { load(); }, []);

  const current = data[section] || {};

  const setField = (name, value) => {
    setData((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [name]: value } }));
  };

  const onUpload = async (fieldName, file) => {
    if (!file) return;
    setUploading(fieldName);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/site-settings/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
      setField(fieldName, fullUrl);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo subir la imagen");
    } finally { setUploading(null); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { [section]: data[section] || {} };
      await api.put("/site-settings", payload);
      toast.success("Sección guardada");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  return (
    <Card className="rounded-2xl" data-testid="site-editor">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="w-64" data-testid="site-section"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SITE_SECTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={save} disabled={busy} className="rounded-full ml-auto" data-testid="site-save">
            <Save className="w-4 h-4 mr-1" /> {busy ? "Guardando…" : "Guardar sección"}
          </Button>
        </div>

        <div className="space-y-4">
          {SITE_FIELDS.map((f) => {
            const value = current[f.name] || "";
            if (f.type === "textarea") {
              return (
                <div key={f.name}>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">{f.label}</label>
                  <Textarea rows={4} className="mt-1" value={value} onChange={(e) => setField(f.name, e.target.value)} data-testid={`site-${section}-${f.name}`} />
                </div>
              );
            }
            if (f.type === "image") {
              return (
                <div key={f.name} className="rounded-xl border border-border p-3">
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">{f.label}</label>
                  <div className="mt-2 grid md:grid-cols-[1fr,auto] gap-2">
                    <Input placeholder="URL de la imagen (https://… o /api/site-images/…)" value={value} onChange={(e) => setField(f.name, e.target.value)} data-testid={`site-${section}-${f.name}`} />
                    <label className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-full border border-border bg-card hover:bg-muted cursor-pointer text-sm font-semibold whitespace-nowrap">
                      <Upload className="w-4 h-4" />
                      {uploading === f.name ? "Subiendo…" : "Subir archivo"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(f.name, e.target.files?.[0])} data-testid={`site-${section}-${f.name}-file`} />
                    </label>
                  </div>
                  {value && (
                    <img src={value} alt="" className="mt-3 max-h-40 rounded-lg object-cover border border-border" />
                  )}
                </div>
              );
            }
            return (
              <div key={f.name}>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">{f.label}</label>
                <Input className="mt-1" value={value} onChange={(e) => setField(f.name, e.target.value)} data-testid={`site-${section}-${f.name}`} />
              </div>
            );
          })}
        </div>

        <p className="text-xs text-foreground/60">Deja un campo vacío para conservar el contenido por defecto del diseño.</p>
      </CardContent>
    </Card>
  );
}


function CatechistsPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingLevels, setEditingLevels] = useState(null); // {user_id, levels}
  const [showPwd, setShowPwd] = useState({});

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/catechists");
      setItems(data);
    } catch (e) {
      toast.error("No se pudo cargar la lista");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar al catequista ${c.name} (${c.email})? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/users/${c.user_id}`);
      toast.success("Catequista eliminado");
      setItems((prev) => prev.filter((x) => x.user_id !== c.user_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const saveLevels = async () => {
    if (!editingLevels) return;
    if (editingLevels.levels.length === 0) return toast.error("Asigna al menos un sacramento");
    try {
      await api.put(`/admin/catechists/${editingLevels.user_id}/levels`, { levels: editingLevels.levels });
      toast.success("Sacramentos actualizados");
      setEditingLevels(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const resetPwd = async (c) => {
    const np = window.prompt(`Nueva contraseña temporal para ${c.email} (mín. 6 caracteres):`, "Temporal123");
    if (!np || np.length < 6) return;
    try {
      const { data } = await api.post(`/admin/users/${c.user_id}/reset-password`, { new_password: np });
      toast.success(`Contraseña restablecida: ${data.temp_password}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo restablecer");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "2-digit" }); }
    catch { return iso || "—"; }
  };
  const levelLabel = (lvls = []) => {
    if (lvls.length === 2) return "Ambos sacramentos";
    if (lvls.includes("primera_comunion")) return "Primera Comunión";
    if (lvls.includes("confirmacion")) return "Confirmación";
    return "—";
  };

  return (
    <Card className="rounded-2xl" data-testid="catechists-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-foreground/70">{busy ? "Cargando…" : `${items.length} catequistas registrados`}</span>
          <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="cat-reload">Actualizar</Button>
        </div>
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="p-3 font-semibold">Nombre</th>
                <th className="p-3 font-semibold">Correo</th>
                <th className="p-3 font-semibold">Sacramentos</th>
                <th className="p-3 font-semibold">Inscripción</th>
                <th className="p-3 font-semibold">Contraseña temporal</th>
                <th className="p-3 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-foreground/60">No hay catequistas inscritos.</td></tr>
              ) : items.map((c) => (
                <tr key={c.user_id} className="border-t border-border align-top" data-testid={`cat-${c.user_id}`}>
                  <td className="p-3 font-semibold">{c.name}</td>
                  <td className="p-3 text-foreground/70">{c.email}</td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-[10px] uppercase">{levelLabel(c.levels)}</Badge>
                    <button
                      className="ml-2 text-[11px] text-primary underline hover:no-underline"
                      onClick={() => setEditingLevels({ user_id: c.user_id, levels: [...(c.levels || [])] })}
                      data-testid={`edit-lv-${c.user_id}`}
                    >Editar</button>
                  </td>
                  <td className="p-3 text-foreground/70 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="p-3">
                    {c.temp_password ? (
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 rounded bg-muted text-xs" data-testid={`tp-${c.user_id}`}>
                          {showPwd[c.user_id] ? c.temp_password : "•".repeat(c.temp_password.length)}
                        </code>
                        <button
                          className="text-[11px] text-primary underline"
                          onClick={() => setShowPwd((s) => ({ ...s, [c.user_id]: !s[c.user_id] }))}
                          data-testid={`tp-toggle-${c.user_id}`}
                        >
                          {showPwd[c.user_id] ? "Ocultar" : "Ver"}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-foreground/50">Ya cambiada</span>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full"
                      data-testid={`reset-pwd-${c.user_id}`}
                      onClick={() => resetPwd(c)}
                    >
                      Restablecer
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full text-destructive hover:bg-destructive/10 ml-1"
                      data-testid={`del-cat-${c.user_id}`}
                      onClick={() => remove(c)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={!!editingLevels} onOpenChange={(v) => !v && setEditingLevels(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl text-primary">Asignar sacramentos</DialogTitle></DialogHeader>
            {editingLevels && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingLevels.levels.includes("primera_comunion")}
                    onChange={() => setEditingLevels((p) => ({ ...p, levels: p.levels.includes("primera_comunion") ? p.levels.filter((x) => x !== "primera_comunion") : [...p.levels, "primera_comunion"] }))}
                    className="w-4 h-4 accent-primary"
                    data-testid="ed-lv-pc"
                  /> Primera Comunión
                </label>
                <label className="flex items-center gap-2 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingLevels.levels.includes("confirmacion")}
                    onChange={() => setEditingLevels((p) => ({ ...p, levels: p.levels.includes("confirmacion") ? p.levels.filter((x) => x !== "confirmacion") : [...p.levels, "confirmacion"] }))}
                    className="w-4 h-4 accent-primary"
                    data-testid="ed-lv-cf"
                  /> Confirmación
                </label>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingLevels(null)} className="rounded-full">Cancelar</Button>
              <Button onClick={saveLevels} className="rounded-full" data-testid="ed-lv-save">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}


function IntencionesPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/intenciones");
      setItems(data);
    } catch (e) {
      toast.error("No se pudieron cargar las intenciones");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso || "—"; }
  };

  const markRead = async (i) => {
    try {
      await api.put(`/admin/intenciones/${i.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === i.id ? { ...x, leida: true } : x)));
    } catch (e) {
      toast.error("No se pudo marcar como leída");
    }
  };

  const remove = async (i) => {
    if (!window.confirm(`¿Eliminar la intención de ${i.nombre}?`)) return;
    try {
      await api.delete(`/admin/intenciones/${i.id}`);
      toast.success("Intención eliminada");
      setItems((prev) => prev.filter((x) => x.id !== i.id));
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const unread = items.filter((i) => !i.leida).length;

  return (
    <Card className="rounded-2xl" data-testid="intenciones-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <span className="text-sm text-foreground/70">
            {busy ? "Cargando…" : `${unread} sin leer · ${items.length} en total`}
          </span>
          <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="int-reload">Actualizar</Button>
        </div>
        {items.length === 0 && !busy && (
          <p className="text-sm text-foreground/60">Aún no hay intenciones recibidas.</p>
        )}
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.id} className={`rounded-xl border p-4 ${i.leida ? "border-border bg-card" : "border-secondary/40 bg-secondary/5"}`} data-testid={`int-${i.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div className="min-w-0">
                  <p className="font-display text-lg text-primary leading-tight">{i.nombre}</p>
                  <p className="text-xs text-foreground/60">
                    Acudiente de <strong>{i.acudiente_de}</strong> · Curso <strong>{i.curso}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!i.leida && <Badge className="bg-secondary text-secondary-foreground uppercase text-[10px] tracking-wider">Nueva</Badge>}
                  <span className="text-xs text-foreground/60 whitespace-nowrap">{fmt(i.created_at)}</span>
                </div>
              </div>
              <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed mt-2 border-l-4 border-primary/30 pl-3 italic">
                “{i.intencion}”
              </p>
              <div className="flex justify-end gap-2 mt-3">
                {!i.leida && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => markRead(i)} data-testid={`int-read-${i.id}`}>
                    Marcar como leída
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => remove(i)} data-testid={`int-del-${i.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AdminSecurityQuestionCard() {
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!user?.email) return;
    api.get("/auth/security-question", { params: { email: user.email } })
      .then((r) => {
        if (r.data?.available) setCurrentQuestion(r.data.question);
      })
      .catch(() => {});
  }, [user?.email]);

  const save = async () => {
    if (question.trim().length < 5) return toast.error("La pregunta debe tener al menos 5 caracteres");
    if (answer.trim().length < 2) return toast.error("La respuesta debe tener al menos 2 caracteres");
    setBusy(true);
    try {
      await api.put("/auth/security-question", { question: question.trim(), answer: answer.trim() });
      toast.success("Pregunta de seguridad actualizada");
      setCurrentQuestion(question.trim());
      setQuestion("");
      setAnswer("");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-2xl" data-testid="admin-sq-card">
      <CardContent className="p-5 space-y-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-secondary">Auto-rescate</p>
          <h3 className="font-display text-xl text-primary">Tu pregunta de seguridad</h3>
          <p className="text-xs text-foreground/60 mt-1">
            Te permite restablecer tu propia contraseña sin ayuda externa desde la pantalla de login.
          </p>
        </div>
        {currentQuestion && !editing && (
          <div className="rounded-xl border border-border p-3 bg-muted/30">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-foreground/60">Pregunta actual</p>
            <p className="font-display text-base text-primary mt-1" data-testid="admin-sq-current">{currentQuestion}</p>
          </div>
        )}
        {editing ? (
          <div className="space-y-3">
            <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Nueva pregunta de seguridad" data-testid="admin-sq-input-q" />
            <Input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Respuesta (no se distingue mayúsculas/minúsculas)" type="text" data-testid="admin-sq-input-a" />
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy} className="rounded-full" data-testid="admin-sq-save">
                {busy ? "Guardando…" : "Guardar"}
              </Button>
              <Button variant="outline" onClick={() => { setEditing(false); setQuestion(""); setAnswer(""); }} className="rounded-full">
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setEditing(true)} className="rounded-full" data-testid="admin-sq-edit">
            {currentQuestion ? "Cambiar pregunta" : "Configurar pregunta"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}


function PendingStudentsPanel({ groups = [] }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(null); // {user_id, name, email}
  const [approveLevel, setApproveLevel] = useState("primera_comunion");
  const [approveGroup, setApproveGroup] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/pending-students");
      setItems(data);
    } catch {
      toast.error("No se pudieron cargar los registros");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso || "—"; } };

  const filteredGroups = groups.filter((g) => g.level === approveLevel);

  const openApprove = (item) => {
    setApproving(item);
    setApproveLevel("primera_comunion");
    setApproveGroup("");
  };

  const doApprove = async () => {
    if (!approving) return;
    try {
      await api.post(`/admin/students/${approving.user_id}/approve`, {
        level: approveLevel,
        group_code: approveGroup || undefined,
      });
      toast.success(`${approving.name} aprobado/a y asignado/a a ${approveLevel === "primera_comunion" ? "Primera Comunión" : "Confirmación"}`);
      setItems((prev) => prev.filter((x) => x.user_id !== approving.user_id));
      setApproving(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo aprobar");
    }
  };

  const doReject = async (item) => {
    if (!window.confirm(`¿Rechazar el registro de ${item.name} (${item.email})?`)) return;
    try {
      await api.post(`/admin/students/${item.user_id}/reject`);
      toast.success("Registro rechazado");
      setItems((prev) => prev.filter((x) => x.user_id !== item.user_id));
    } catch {
      toast.error("No se pudo rechazar");
    }
  };

  const doDelete = async (item) => {
    if (!window.confirm(`¿Eliminar definitivamente el registro de ${item.name}?`)) return;
    try {
      await api.delete(`/admin/users/${item.user_id}`);
      toast.success("Registro eliminado");
      setItems((prev) => prev.filter((x) => x.user_id !== item.user_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <Card className="rounded-2xl" data-testid="pending-students-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-foreground/70">{busy ? "Cargando…" : `${items.length} estudiante(s) en espera`}</span>
          <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="pending-reload">Actualizar</Button>
        </div>
        {items.length === 0 && !busy ? (
          <p className="text-sm text-foreground/60">No hay registros pendientes en este momento.</p>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="p-3 font-semibold">Nombre</th>
                  <th className="p-3 font-semibold">Correo</th>
                  <th className="p-3 font-semibold">Fecha de registro</th>
                  <th className="p-3 font-semibold">Estado</th>
                  <th className="p-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.user_id} className="border-t border-border align-top" data-testid={`pend-${s.user_id}`}>
                    <td className="p-3 font-semibold">{s.name}</td>
                    <td className="p-3 text-foreground/70 break-all">{s.email}</td>
                    <td className="p-3 text-foreground/70 whitespace-nowrap">{fmt(s.created_at)}</td>
                    <td className="p-3">
                      <Badge className="bg-secondary text-secondary-foreground uppercase text-[10px] tracking-wider">Pendiente</Badge>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" className="rounded-full" onClick={() => openApprove(s)} data-testid={`approve-${s.user_id}`}>
                        <UserCheck className="w-3.5 h-3.5 mr-1" /> Aprobar
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full ml-1" onClick={() => doReject(s)} data-testid={`reject-${s.user_id}`}>
                        Rechazar
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full text-destructive ml-1" onClick={() => doDelete(s)} data-testid={`del-pend-${s.user_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={!!approving} onOpenChange={(v) => !v && setApproving(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-primary">Aprobar estudiante</DialogTitle>
            </DialogHeader>
            {approving && (
              <div className="space-y-4">
                <div className="rounded-xl border border-border p-3 bg-muted/30">
                  <p className="font-semibold">{approving.name}</p>
                  <p className="text-xs text-foreground/60">{approving.email}</p>
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Asignar sacramento</label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => { setApproveLevel("primera_comunion"); setApproveGroup(""); }}
                      className={`p-3 rounded-xl border ${approveLevel === "primera_comunion" ? "border-primary bg-primary/10" : "border-border"}`}
                      data-testid="approve-level-pc"
                    >Primera Comunión</button>
                    <button
                      type="button"
                      onClick={() => { setApproveLevel("confirmacion"); setApproveGroup(""); }}
                      className={`p-3 rounded-xl border ${approveLevel === "confirmacion" ? "border-primary bg-primary/10" : "border-border"}`}
                      data-testid="approve-level-cf"
                    >Confirmación</button>
                  </div>
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Grupo (opcional)</label>
                  <Select value={approveGroup || "_none"} onValueChange={(v) => setApproveGroup(v === "_none" ? "" : v)}>
                    <SelectTrigger className="mt-2" data-testid="approve-group"><SelectValue placeholder="Sin grupo (se puede asignar después)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Sin grupo</SelectItem>
                      {filteredGroups.map((g) => (
                        <SelectItem key={g.code} value={g.code}>{g.code} · {g.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproving(null)} className="rounded-full">Cancelar</Button>
              <Button onClick={doApprove} className="rounded-full" data-testid="approve-submit">Confirmar aprobación</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

